import { Grid } from 'components/grid'
import { computed, defineComponent } from 'vue'
import { AssetImageV2 } from 'components/asset-image-v2'
import type { IInvestmentInfoTooltip } from './types'
import { twColor } from '@/utils/style'
import TinyBadge from 'components/tiny-badge'
import DecoratedNumber from 'components/decorated-number'
import { prettyAssetSource, prettyAssetType } from '@/utils/asset'
import { EAssetSource, EAssetType } from 'findef'
import {
  getInvestmentCurrentValue,
  getInvestmentCurrentValueCurrency,
  getInvestmentInitialCurrency,
  getInvestmentInitialValue
} from '@/utils/investment'
import { useUserSettings } from '@/modules/settings/hooks/useUserSettings'
import { computeRoi } from '@/utils/roi'
import { numberToHumanReadable } from '@/utils/formatting/number'
import { useForexTickers } from '@/integrations/findex/hooks/forex/useForexTickers'

const InvestmentInfoTooltip = defineComponent<IInvestmentInfoTooltip>({
  setup(props) {
    const forex = props.forex || useForexTickers()
    const settings = useUserSettings()

    const name = computed(() => (props.investment).name ?? props.investment.asset.name)
    const source = computed(() =>
      prettyAssetSource(props.investment.asset.source || EAssetSource.MANUAL)
    )

    const currentValue = computed(() => getInvestmentCurrentValue(props.investment))
    const initialValue = computed(() => getInvestmentInitialValue(props.investment))
    const currentValueCurrency = computed(() => getInvestmentCurrentValueCurrency(props.investment))
    const initialValueCurrency = computed(() => getInvestmentInitialCurrency(props.investment))

    const tickerCurrent = computed(() =>
      forex.getTickerRef(currentValueCurrency.value || 'SEK', settings.currency.value)
    )
    const tickerInitial = computed(() =>
      forex.getTickerRef(initialValueCurrency.value || 'SEK', settings.currency.value)
    )

    const badgeVariant = computed(() => {
      if (props.investment.provider || props.investment.providerImport) return 'blue';
      return props.investment.asset.source === EAssetSource.IR ? 'purple' : 'gray'
    })

    const roi = computed(() => {
      return computeRoi(
        initialValue.value * tickerInitial.value.value.bid,
        currentValue.value * tickerCurrent.value.value.bid
      )
    })

    const typename = computed(() => {
      const asset = props.investment.asset

      return prettyAssetType(
        asset.type || EAssetType.EQUITY,
        asset.listed,
        asset.subtypes,
        false,
        true
      )
    })

    return () => {
      return (
        <div class="min-w-[16rem]">
          <Grid
            columns="max-content auto"
            class="h-16 p-3 bg-gray-100 justify-start items-start gap-2"
            alignItems="center"
          >
            <div
              class="border border-gray-300 justify-center items-center inline-flex"
              style={{
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <AssetImageV2
                  size="full"
                  variant="gray"
                  investment={props.investment}
                  asset={props.investment.asset}
                  iconProps={{ size: "1.5rem", fill: twColor('gray-400') }}
                />
              </div>
            </div>
            <Grid noDefaultLayout rows="max-content max-content" alignItems="center">
              <div class="text-gray-900 text-xs font-semibold font-['Inter'] leading-[18px]">
                {name.value}
              </div>
              <div class="text-gray-500 text-xs font-normal font-['Inter'] leading-[18px]">
                {typename.value}
              </div>
            </Grid>
          </Grid>
          <div class="bg-white space-y-2 pb-2">
            <Grid
              noDefaultLayout
              alignItems="center"
              columns="100%"
              class="border-b border-gray-200 px-3 py-3 w-full"
            >
              <Grid
                columns="max-content max-content"
                justifyContent="space-between"
                alignItems="center"
              >
                <div class="text-gray-900 text-xs font-medium font-['Inter'] leading-3">
                  Maintained
                </div>
                <div class="text-gray-500 text-xs font-normal font-['Inter'] leading-3">
                  <TinyBadge variant={badgeVariant.value} size="m">{source.value}</TinyBadge>
                </div>
              </Grid>
            </Grid>
            <Grid noDefaultLayout alignItems="center" columns="100%" class="px-3 py-1 w-full">
              <Grid
                columns="max-content max-content"
                justifyContent="space-between"
                alignItems="center"
              >
                <div class="text-gray-900 text-xs font-medium font-['Inter'] leading-3">
                  Current Value
                </div>
                <div class="text-gray-500 text-xs font-normal font-['Inter'] leading-3">
                  {numberToHumanReadable(
                    currentValue.value * tickerCurrent.value.value.bid,
                    6,
                    settings.currency.value,
                    [],
                    false,
                    0
                  )}
                </div>
              </Grid>
            </Grid>

            <Grid noDefaultLayout alignItems="center" columns="100%" class="px-3 py-1 w-full">
              <Grid
                columns="max-content max-content"
                justifyContent="space-between"
                alignItems="center"
              >
                <div class="text-gray-900 text-xs font-medium font-['Inter'] leading-3">ROI</div>
                <div class="text-gray-500 text-xs font-normal font-['Inter'] leading-3">
                  <DecoratedNumber
                    iconSize={10}
                    valueClass="text-xs"
                    arrow
                    value={roi.value}
                    unit="percent"
                    precision={1}
                    signs={['-', '+']}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      )
    }
  }
})

InvestmentInfoTooltip.props = ['investment', 'forex']

export default InvestmentInfoTooltip
